import { useState, useCallback } from 'react';
import { AppError, ErrorType, handleError } from '@/utils/error/errorHandler';
import { useToast } from '@/components/ui/use-toast';
import { PoemHighlight } from '@/types/highlights';
import { getHighlightLogger, HighlightLogContext } from '@/utils/logging/browserLogger';
import { ApiProvider } from '@/types/sharedTypes';
import { generateHighlightsWithGPT, generateHighlightsWithClaude } from '@/services/highlightGenerationService';
import { generateAndSaveHighlightsUnified } from '@/services/highlightService';

export type HighlightGenerationStatus = 'idle' | 'loading' | 'success' | 'error';

export interface UseHighlightGenerationResult {
  /**
   * Current status of the highlight generation process
   */
  status: HighlightGenerationStatus;
  
  /**
   * Error that occurred during generation, if any
   */
  error: Error | AppError | null;
  
  /**
   * Number of highlights successfully generated
   */
  highlightCount: number;
  
  /**
   * Function to generate highlights using our unified approach
   */
  generateHighlights: (
    poemId: string,
    poemContent: string[],
    poetName: string,
    stylePoints?: string[],
    apiProvider?: ApiProvider
  ) => Promise<PoemHighlight[]>;
  
  /**
   * Function to clear the current status and reset the state
   */
  clearStatus: () => void;
}

/**
 * Custom hook to manage highlight generation with appropriate UI feedback
 * @param context - The context in which highlights are being generated ('admin' or 'generate')
 * @returns Functions and state for handling highlight generation
 */
export function useHighlightGeneration(
  context: HighlightLogContext
): UseHighlightGenerationResult {
  const [status, setStatus] = useState<HighlightGenerationStatus>('idle');
  const [error, setError] = useState<Error | AppError | null>(null);
  const [highlightCount, setHighlightCount] = useState<number>(0);
  const toast = useToast();
  const logger = getHighlightLogger(context);
  
  /**
   * Clears the current status and resets the state
   */
  const clearStatus = useCallback(() => {
    setStatus('idle');
    setError(null);
    setHighlightCount(0);
  }, []);
  
  /**
   * Generates highlights for a poem using our unified approach
   * @param poemId - The ID of the poem
   * @param poemContent - The content of the poem as an array of lines
   * @param poetName - The name of the poet
   * @param stylePoints - Optional style points for highlight generation
   * @param apiProvider - The AI provider to use (default: ANTHROPIC)
   * @returns The generated highlights
   */
  const generateHighlights = useCallback(
    async (
      poemId: string,
      poemContent: string[],
      poetName: string,
      stylePoints: string[] = [],
      apiProvider: ApiProvider = ApiProvider.ANTHROPIC
    ): Promise<PoemHighlight[]> => {
      // Reset the state
      setStatus('loading');
      setError(null);
      setHighlightCount(0);
      
      logger.info('Starting highlight generation', {
        poemId,
        poetName,
        contentLength: poemContent.length,
        stylePointCount: stylePoints.length,
        apiProvider: apiProvider === ApiProvider.ANTHROPIC ? 'Claude' : 'GPT',
        context
      });
      
      try {
        // Use the unified highlight generation function
        const highlights = await generateAndSaveHighlightsUnified(
          poemId,
          poemContent,
          poetName,
          { stylePoints },
          apiProvider,
          context
        );
        
        // Update the state
        setStatus('success');
        setHighlightCount(highlights.length);
        
        // Log success
        logger.info('Highlight generation completed successfully', {
          poemId,
          highlightCount: highlights.length,
          apiProvider: apiProvider === ApiProvider.ANTHROPIC ? 'Claude' : 'GPT'
        });
        
        // Show a toast notification for success
        if (context === 'admin') {
          toast.toast({
            title: 'Highlights Generated',
            description: `Generated ${highlights.length} highlights for the poem.`,
            variant: 'default'
          });
        }
        
        return highlights;
      } catch (err) {
        // Update the state
        setStatus('error');
        const errorObj = err instanceof Error ? err : new Error(String(err));
        setError(errorObj);
        
        // Log the error
        logger.error('Highlight generation failed', errorObj, {
          poemId,
          poetName,
          apiProvider: apiProvider === ApiProvider.ANTHROPIC ? 'Claude' : 'GPT'
        });
        
        // Show a toast notification for error in admin context only
        // Generate context will show the error in the UI instead
        if (context === 'admin') {
          toast.toast({
            title: 'Error Generating Highlights',
            description: errorObj.message || 'An unknown error occurred during highlight generation.',
            variant: 'destructive'
          });
        }
        
        // Re-throw the error instead of returning an empty array
        throw errorObj;
      }
    },
    [context, logger, toast]
  );
  
  return {
    status,
    error,
    highlightCount,
    generateHighlights,
    clearStatus
  };
} 